import { useState } from 'react'
import { useGoogleLogin } from '@react-oauth/google'
import { Brand } from '../../../shared/components/Brand'
import { nerdleAuth, readNerdleIdentity } from '../../../shared/labs/panelAuth'
import { savePanelSignup } from '../../../shared/labs/sessionStore'

// Bespoke, labs-native re-engagement prompt, shown AFTER the feedback step to LOGGED-OUT
// players (logged-in players are captured silently on the summary). Google + email only,
// no Facebook. On success the identity is captured to the labsPanel side-channel.
export const PanelSignup = ({ sessionId, onDone }: { sessionId: string; onDone: () => void }) => {
  const [showEmail, setShowEmail] = useState(false)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const capture = async (method: 'google' | 'email', capturedEmail: string | null) => {
    const id = readNerdleIdentity()
    try {
      await savePanelSignup({
        sessionId,
        method,
        lblToken: id?.lblToken ?? null,
        email: capturedEmail ?? id?.email ?? null,
        marketingOptIn: true,
      })
    } catch {
      /* best-effort capture */
    }
    setDone(true)
  }

  const googleLogin = useGoogleLogin({
    flow: 'implicit',
    onSuccess: async (codeResponse) => {
      setBusy(true)
      setMsg(null)
      try {
        const info = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${codeResponse.access_token}` },
        }).then((r) => r.json())
        const r = await nerdleAuth(
          { provider: 'google', googleId: info.sub, fullname: info.name, email: info.email, source: 'nerdle' },
          {}
        )
        if (r.ok) await capture('google', info.email)
        else setMsg(r.message || 'Sign-in failed.')
      } catch {
        setMsg('Sign-in failed.')
      } finally {
        setBusy(false)
      }
    },
    onError: () => setMsg('Sign-in failed.'),
  })

  const submitEmail = async () => {
    if (!name.trim()) {
      setMsg('Please enter your name.')
      return
    }
    if (!email.includes('@') || password.length < 4) {
      setMsg('Enter an email and a password (at least 4 characters).')
      return
    }
    setBusy(true)
    setMsg(null)
    // create the account (fullname is required); nerdleAuth logs in on success, and also
    // logs in a returning user if the account already exists. Any other error is surfaced.
    const r = await nerdleAuth({ email, password, fullname: name, source: 'nerdle' }, { signup: true })
    if (r.ok) await capture('email', email)
    else setMsg(r.message || 'Could not sign you up. Please try again.')
    setBusy(false)
  }

  return (
    <div className="flex flex-col items-center px-6 pt-10 pb-8">
      <Brand game="twentyfour" />
      <div className="mt-8 w-full max-w-md text-center">
        {done ? (
          <>
            <div className="mb-2 text-5xl">📬</div>
            <h2 className="text-xl font-semibold text-gray-800">You're on the list</h2>
            <p className="mt-2 text-sm text-gray-500">We'll email you when the next labs game is ready.</p>
            <button
              type="button"
              onClick={onDone}
              className="mt-6 w-full rounded-lg bg-[#820458] py-3 font-semibold text-white"
            >
              Continue
            </button>
          </>
        ) : (
          <>
            <div className="mb-2 text-5xl">🎮</div>
            <h2 className="text-xl font-semibold text-gray-800">Hear about the next labs game</h2>
            <p className="mt-2 text-sm text-gray-600">
              Sign up for an account to hear about the next labs game and more news from nerdle.
            </p>

            <button
              type="button"
              disabled={busy}
              onClick={() => googleLogin()}
              className="mt-5 w-full rounded-lg border border-gray-300 bg-white py-3 font-semibold text-gray-700 shadow-sm disabled:opacity-50"
            >
              Continue with Google
            </button>

            {!showEmail ? (
              <button type="button" onClick={() => setShowEmail(true)} className="mt-3 text-sm text-gray-500 underline">
                Use email instead
              </button>
            ) : (
              <div className="mt-4 space-y-2 text-left">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Name"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-[#820458] focus:outline-none"
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-[#820458] focus:outline-none"
                />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Choose a password"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-[#820458] focus:outline-none"
                />
                <button
                  type="button"
                  disabled={busy}
                  onClick={submitEmail}
                  className="w-full rounded-lg bg-[#820458] py-3 font-semibold text-white disabled:opacity-50"
                >
                  Sign up
                </button>
              </div>
            )}

            {msg && <p className="mt-3 text-sm text-red-500">{msg}</p>}

            <button type="button" onClick={onDone} className="mt-4 block w-full text-sm text-gray-400 underline">
              No thanks
            </button>
          </>
        )}
      </div>
    </div>
  )
}
